import React, { useContext, useEffect } from 'react';
import { useParams, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  AppBar,
  Toolbar,
  Box,
  Button,
  ButtonBase,
  Hidden
} from '@material-ui/core';
import styled from 'styled-components';

import {
  AuthContext,
  filterOrgEnabledPageEntries,
  useFile,
  useSpot
} from 'framework';
import {
  CroppedImage,
  LinkButton,
  SignedInUserMenu,
  Spacer
} from '../components';

import { pages } from './pages';

import logo from '../images/logo.png';

const StyledToolbar = styled(Toolbar)`
  display: flex;
`;

const StyledCompanyName = styled(ButtonBase)`
  padding: ${p => p.theme.spacing(0.5, 1)};
`;

const StyledLogo = styled(Box)`
  margin-right: ${p => p.theme.spacing(2)}px;
  background-color: #fff;
  border-radius: 50%;
  width: 48px;
  height: 48px;
`;

const TrialBanner = styled(Box)<{ $variant: 'info' | 'warning' }>`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing(2)}px;
  padding: ${p => p.theme.spacing(1, 2)};
  background: ${p =>
    p.$variant === 'warning' ? '#fff4d6' : 'rgba(255, 255, 255, 0.14)'};
  color: ${p =>
    p.$variant === 'warning'
      ? p.theme.palette.text.primary
      : p.theme.palette.primary.contrastText};
`;

const TrialBannerAction = styled(Button)`
  white-space: nowrap;
`;

export const Header = () => {
  const history = useHistory();
  const { isInitialized, isSignedIn, getGroups } = useContext(AuthContext);
  const { organization } = useParams<{ organization: string }>();
  const { t } = useTranslation();

  const { data, query } = useSpot();
  const initialized = isInitialized();

  useEffect(() => {
    if (!initialized) {
      return;
    }
    query(`org/${organization}`, {}, ['organizations', organization]);
  }, [organization, query, initialized]);

  const org = data?.organizations?.[organization];
  const trialEndsAt = org?.stripe?.trialEndsAt;
  const isReadOnly = org?.stripe?.accessMode === 'read_only';
  const showTrialBanner = !!trialEndsAt || isReadOnly;
  const canAdministerBilling = isInitialized()
    ? getGroups().includes('administrators')
    : false;

  const { url: imageUrl } = useFile(org?.icon || '', organization);

  const visiblePageEntries = filterOrgEnabledPageEntries(
    Object.entries(pages),
    org
  );

  return (
    <AppBar position="fixed">
      <StyledToolbar>
        <StyledCompanyName
          onClick={() =>
            org ? history.push(`/${organization}`) : history.push('/')
          }
        >
          {org?.icon ? (
            <StyledLogo>
              <CroppedImage height={48} width={48} url={imageUrl} />
            </StyledLogo>
          ) : (
            <Box mr={2}>
              <img src={logo} alt={t('logo')} height={48} width={48} />
            </Box>
          )}
          <Hidden smDown>
            <Typography variant="h5">{org?.name || organization}</Typography>
          </Hidden>
        </StyledCompanyName>
        <Spacer />
        <Hidden smDown>
          {visiblePageEntries
            .map(([, innerPage]) => innerPage)
            .filter(innerPage => !innerPage.hidden)
            .filter(innerPage => {
              if (!innerPage.authorized) {
                return true;
              }

              if (!isInitialized() || !isSignedIn()) {
                return false;
              }

              const userGroups = isInitialized() ? getGroups() : [];
              return innerPage.groups?.length
                ? innerPage.groups?.some(group => userGroups.includes(group))
                : true;
            })
            .map(innerPage => (
              <LinkButton
                key={innerPage.path}
                title={innerPage.title && t(innerPage.title)}
                path={`${innerPage.path
                  .replaceAll(':organization', organization)
                  .replaceAll(':userId', '')
                  .replaceAll(':tab?', '')}`}
                icon={innerPage.icon}
              />
            ))}
        </Hidden>
        {isInitialized() && isSignedIn() && <SignedInUserMenu />}
      </StyledToolbar>
      {showTrialBanner ? (
        <TrialBanner $variant={isReadOnly ? 'warning' : 'info'}>
          <Typography variant="body2">
            {isReadOnly
              ? t('trialReadOnlyBanner')
              : t('trialActiveBanner', {
                  date: trialEndsAt
                    ? new Intl.DateTimeFormat(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }).format(new Date(trialEndsAt))
                    : t('unknown')
                })}
          </Typography>
          <Spacer />
          {isReadOnly && canAdministerBilling ? (
            <TrialBannerAction
              color="primary"
              size="small"
              variant="contained"
              onClick={() => history.push(`/${organization}/admin/billing`)}
            >
              {t('restoreBilling')}
            </TrialBannerAction>
          ) : null}
        </TrialBanner>
      ) : null}
    </AppBar>
  );
};
